import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

import {
  clampZoom,
  DEFAULT_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  panBy,
  recenterViewportPreservingZoom,
  screenToStage,
  stepZoom,
  VIEWPORT_PADDING,
  viewportAfterStageSizeChange,
  zoomAtViewportCenter,
  type Point,
  type Size,
  type ViewportState,
} from '../canvas/viewport';

export type CanvasViewport = {
  viewport: ViewportState;
  canZoomIn: boolean;
  canZoomOut: boolean;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetViewport: () => void;
  panByScreen: (delta: Point) => void;
  clientToStage: (clientX: number, clientY: number) => Point;
  /** Space is held, so the next press pans instead of selecting. */
  isPanArmed: boolean;
  isPanning: boolean;
  /** Space only arms panning while the pointer or the focus is on the stage. */
  canArmPan: (target: EventTarget | null) => boolean;
  setPanArmed: (armed: boolean) => void;
  /** Starts a pan when Space is held. Returns whether the press was taken. */
  beginPan: (event: ReactPointerEvent) => boolean;
  /** Returns whether a pan is in progress and consumed the move. */
  movePan: (event: ReactPointerEvent) => boolean;
  endPan: () => void;
};

export function useCanvasViewport({
  stageRef,
  frame,
  stageSize,
}: {
  stageRef: RefObject<HTMLElement>;
  frame: Size;
  stageSize: Size;
}): CanvasViewport {
  const { width: frameWidth, height: frameHeight } = frame;
  const { width: stageWidth, height: stageHeight } = stageSize;

  const [viewport, setViewport] = useState<ViewportState>(
    () =>
      viewportAfterStageSizeChange(stageSize, frame, DEFAULT_ZOOM, VIEWPORT_PADDING) ?? {
        panX: 0,
        panY: 0,
        zoom: DEFAULT_ZOOM,
      },
  );
  const [isPanArmed, setIsPanArmed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panArmedRef = useRef(false);
  const pointerOverStageRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const layoutRef = useRef({ frameWidth, frameHeight, stageWidth, stageHeight });

  useLayoutEffect(() => {
    const previous = layoutRef.current;
    const frameChanged = previous.frameWidth !== frameWidth || previous.frameHeight !== frameHeight;
    const stageChanged = previous.stageWidth !== stageWidth || previous.stageHeight !== stageHeight;
    layoutRef.current = { frameWidth, frameHeight, stageWidth, stageHeight };

    const nextFrame = { width: frameWidth, height: frameHeight };
    const nextStage = { width: stageWidth, height: stageHeight };

    if (frameChanged) {
      if (nextStage.width > 0 && nextStage.height > 0) {
        setViewport(
          recenterViewportPreservingZoom(nextFrame, nextStage, DEFAULT_ZOOM, VIEWPORT_PADDING),
        );
      }
      return;
    }

    if (stageChanged) {
      setViewport(
        (current) =>
          viewportAfterStageSizeChange(nextStage, nextFrame, current.zoom, VIEWPORT_PADDING) ??
          current,
      );
    }
  }, [frameWidth, frameHeight, stageWidth, stageHeight]);

  const clientToStage = useCallback(
    (clientX: number, clientY: number) => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) {
        return { x: 0, y: 0 };
      }
      return screenToStage({ x: clientX - rect.left, y: clientY - rect.top }, viewport);
    },
    [stageRef, viewport],
  );

  const zoomAroundCenter = useCallback(
    (nextZoom: (zoom: number) => number) => {
      const stage = {
        width: stageRef.current?.clientWidth || stageWidth,
        height: stageRef.current?.clientHeight || stageHeight,
      };
      setViewport((current) => {
        const zoom = nextZoom(current.zoom);
        return zoom === current.zoom ? current : zoomAtViewportCenter(current, stage, zoom);
      });
    },
    [stageRef, stageWidth, stageHeight],
  );

  const setZoom = useCallback(
    (zoom: number) => zoomAroundCenter(() => clampZoom(zoom)),
    [zoomAroundCenter],
  );
  const zoomIn = useCallback(
    () => zoomAroundCenter((zoom) => stepZoom(zoom, 1)),
    [zoomAroundCenter],
  );
  const zoomOut = useCallback(
    () => zoomAroundCenter((zoom) => stepZoom(zoom, -1)),
    [zoomAroundCenter],
  );

  const resetViewport = useCallback(() => {
    setViewport(
      recenterViewportPreservingZoom(
        { width: frameWidth, height: frameHeight },
        { width: stageWidth, height: stageHeight },
        DEFAULT_ZOOM,
        VIEWPORT_PADDING,
      ),
    );
  }, [frameWidth, frameHeight, stageWidth, stageHeight]);

  const panByScreen = useCallback((delta: Point) => {
    setViewport((current) => panBy(current, delta));
  }, []);

  const setPanArmed = useCallback((armed: boolean) => {
    panArmedRef.current = armed;
    setIsPanArmed(armed);
  }, []);

  const canArmPan = useCallback(
    (target: EventTarget | null) => {
      if (panArmedRef.current || pointerOverStageRef.current) {
        return true;
      }
      const stage = stageRef.current;
      return Boolean(stage && target instanceof Node && stage.contains(target));
    },
    [stageRef],
  );

  const beginPan = useCallback(
    (event: ReactPointerEvent) => {
      if (!panArmedRef.current) {
        return false;
      }
      event.preventDefault();
      panStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        panX: viewport.panX,
        panY: viewport.panY,
      };
      setIsPanning(true);
      return true;
    },
    [viewport.panX, viewport.panY],
  );

  const movePan = useCallback((event: ReactPointerEvent) => {
    const start = panStartRef.current;
    if (!start) {
      return false;
    }
    const panX = start.panX + event.clientX - start.x;
    const panY = start.panY + event.clientY - start.y;
    setViewport((current) => ({ ...current, panX, panY }));
    return true;
  }, []);

  const endPan = useCallback(() => {
    panStartRef.current = null;
    setIsPanning(false);
  }, []);

  // Re-binds on stage size changes because the stage node only mounts once it has a size.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) {
      return undefined;
    }
    const onPointerEnter = () => {
      pointerOverStageRef.current = true;
    };
    const onPointerLeave = () => {
      pointerOverStageRef.current = false;
    };
    // Bound to the stage node so page scroll outside the canvas is unchanged.
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      event.preventDefault();
      panByScreen({ x: -event.deltaX, y: -event.deltaY });
    };
    node.addEventListener('pointerenter', onPointerEnter);
    node.addEventListener('pointerleave', onPointerLeave);
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      node.removeEventListener('pointerenter', onPointerEnter);
      node.removeEventListener('pointerleave', onPointerLeave);
      node.removeEventListener('wheel', onWheel);
    };
  }, [panByScreen, stageRef, stageWidth, stageHeight]);

  return {
    viewport,
    canZoomIn: viewport.zoom < MAX_ZOOM,
    canZoomOut: viewport.zoom > MIN_ZOOM,
    setZoom,
    zoomIn,
    zoomOut,
    resetViewport,
    panByScreen,
    clientToStage,
    isPanArmed,
    isPanning,
    canArmPan,
    setPanArmed,
    beginPan,
    movePan,
    endPan,
  };
}
